import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import FormField from '../../components/ui/FormField';
import Button from '../../components/ui/Button';
import NumericInput from '../../components/NumericInput';
import type { SchemaFieldProps } from '../types';
import './PricingTiersField.css';

/**
 * A single time-bound pricing tier persisted in the membership `prices` jsonb.
 */
export interface PriceTier {
  durationDays: number;
  price: number;
  label: string;
  sortOrder: number;
  isDefault: boolean;
  /**
   * UI-only flag marking a tier as a custom (user-added) duration rather than
   * one of the four standard tiers. Drives whether the row's duration is
   * editable and removable. Never persisted — stripped before the payload is
   * sent to the backend (see MembershipForm.handleSubmit).
   */
  isCustom?: boolean;
}

/**
 * Composite value carried by the `prices` field. The pricing-tier UI also owns
 * the membership's yearly price (it auto-generates tiers from it), so both are
 * threaded through this one field's value — the MembershipForm wrapper unpacks
 * `yearlyPrice` and `prices` back into separate top-level payload keys.
 */
export interface PricingTiersValue {
  yearlyPrice: number;
  prices: PriceTier[];
  /** True once the user edits a tier directly; suspends auto-generation. */
  manuallyEdited: boolean;
}

export const STANDARD_DURATIONS = [30, 90, 180, 365];

/**
 * Auto-generate the four standard pricing tiers from a yearly price. Mirrors the
 * backend's generateDefaultPrices so the create/edit payload is unchanged.
 */
export const generateDefaultPrices = (yearlyPrice: number): PriceTier[] => {
  if (!yearlyPrice || yearlyPrice <= 0) return [];
  return [
    { durationDays: 30, price: Math.round((yearlyPrice / 12) * 100) / 100, label: '1 Month', sortOrder: 1, isDefault: false, isCustom: false },
    { durationDays: 90, price: Math.round((yearlyPrice / 4) * 100) / 100, label: '3 Months', sortOrder: 2, isDefault: false, isCustom: false },
    { durationDays: 180, price: Math.round((yearlyPrice / 2) * 100) / 100, label: '6 Months', sortOrder: 3, isDefault: false, isCustom: false },
    { durationDays: 365, price: yearlyPrice, label: '12 Months', sortOrder: 4, isDefault: true, isCustom: false },
  ];
};

/**
 * Custom SchemaForm widget for the membership `prices` jsonb field (time type).
 *
 * Extracted verbatim (behaviour-wise) from MembershipForm's former inline
 * yearly-price input + pricing-tier rows: editing the yearly price regenerates
 * the standard tiers until the user touches a tier directly, after which the
 * tiers are preserved. Standard-duration tiers cannot be removed; custom tiers
 * can. The default tier is chosen via a radio per row, and the current default
 * cannot be deleted while other tiers remain.
 *
 * Rebuilt with FormField + NumericInput + ui/Button (no raw inputs/buttons).
 */
const PricingTiersField: React.FC<SchemaFieldProps> = ({ field, value, onChange, error }) => {
  const { t } = useTranslation();

  // Namespace the element ids + radio group by the field name so multiple
  // instances can be rendered on one page (e.g. the course renewal modal's
  // per-option editors) without id/`name` collisions. The ids stay internally
  // consistent per instance (used only for htmlFor/label + radio grouping).
  const uid = field.name || 'prices';
  const yearlyPriceId = `sf-${uid}-yearlyPrice`;
  const tiersId = `sf-${uid}-pricingTiers`;
  const defaultTierName = `sf-${uid}-defaultTier`;

  const state: PricingTiersValue = value ?? { yearlyPrice: 0, prices: [], manuallyEdited: false };
  const { yearlyPrice, prices, manuallyEdited } = state;

  const emit = useCallback(
    (next: Partial<PricingTiersValue>) => {
      onChange({ ...state, ...next });
    },
    [onChange, state]
  );

  const onYearlyPriceChange = useCallback(
    (raw: string) => {
      const nextYearly = raw === '' ? 0 : parseFloat(raw);
      // Auto-regenerate the standard tiers until the user edits a tier directly.
      const nextPrices = manuallyEdited ? prices : generateDefaultPrices(nextYearly);
      onChange({ yearlyPrice: nextYearly, prices: nextPrices, manuallyEdited });
    },
    [manuallyEdited, prices, onChange]
  );

  const updateTierPrice = useCallback(
    (index: number, newPrice: number) => {
      emit({
        manuallyEdited: true,
        prices: prices.map((tier, i) => (i === index ? { ...tier, price: newPrice } : tier)),
      });
    },
    [emit, prices]
  );

  const updateTierLabel = useCallback(
    (index: number, newLabel: string) => {
      emit({
        manuallyEdited: true,
        prices: prices.map((tier, i) => (i === index ? { ...tier, label: newLabel } : tier)),
      });
    },
    [emit, prices]
  );

  const updateTierDuration = useCallback(
    (index: number, newDays: number) => {
      // Clamp to a positive integer; recompute the price from the new duration
      // using the same yearly-rate formula addCustomTier seeds custom tiers with.
      const days = Math.max(1, Math.round(newDays) || 1);
      emit({
        manuallyEdited: true,
        prices: prices.map((tier, i) =>
          i === index
            ? { ...tier, durationDays: days, price: Math.round((yearlyPrice / 365) * days * 100) / 100 }
            : tier
        ),
      });
    },
    [emit, prices, yearlyPrice]
  );

  const setDefaultTier = useCallback(
    (index: number) => {
      emit({
        manuallyEdited: true,
        prices: prices.map((tier, i) => ({ ...tier, isDefault: i === index })),
      });
    },
    [emit, prices]
  );

  const addCustomTier = useCallback(() => {
    const maxSort = prices.length > 0 ? Math.max(...prices.map((tier) => tier.sortOrder)) : 0;
    emit({
      manuallyEdited: true,
      prices: [
        ...prices,
        {
          durationDays: 30,
          price: Math.round((yearlyPrice / 365) * 30 * 100) / 100,
          label: t('memberships.customDuration'),
          sortOrder: maxSort + 1,
          isDefault: false,
          isCustom: true,
        },
      ],
    });
  }, [emit, prices, yearlyPrice, t]);

  const removeCustomTier = useCallback(
    (index: number) => {
      const tier = prices[index];
      // Don't allow removing the default while other tiers remain — the user
      // must pick a new default first.
      if (tier.isDefault && prices.length > 1) return;
      emit({ manuallyEdited: true, prices: prices.filter((_, i) => i !== index) });
    },
    [emit, prices]
  );

  const sortedTiers = [...prices].sort((a, b) => a.sortOrder - b.sortOrder);

  return (
    <>
      <FormField
        htmlFor={yearlyPriceId}
        label={t('memberships.yearlyPrice')}
        required={field.required}
        error={error}
      >
        <NumericInput
          id={yearlyPriceId}
          min="0"
          step="0.01"
          value={yearlyPrice === 0 ? '' : yearlyPrice}
          onChange={(e) => onYearlyPriceChange(e.target.value)}
          required
        />
      </FormField>

      {prices.length > 0 && (
        <FormField htmlFor={tiersId} label={t('memberships.pricingTiers')}>
          <div className="pricing-tiers" id={tiersId}>
            {sortedTiers.map((tier, index) => {
              // Classify by the explicit flag so a custom tier stays editable
              // even when its duration lands on a standard value. Fall back to
              // value-based classification only when the flag is absent (legacy
              // data that predates the flag).
              const isCustom = tier.isCustom ?? !STANDARD_DURATIONS.includes(tier.durationDays);
              return (
                <div key={index} className="pricing-tier-row">
                  <input
                    type="text"
                    value={tier.label}
                    onChange={(e) => updateTierLabel(index, e.target.value)}
                    className="tier-label-input"
                  />
                  {!isCustom ? (
                    <span className="tier-duration">{tier.durationDays}d</span>
                  ) : (
                    <div className="tier-duration-edit">
                      <NumericInput
                        value={tier.durationDays}
                        onChange={(e) => updateTierDuration(index, parseInt(e.target.value, 10) || 1)}
                        min="1"
                        step="1"
                        className="tier-duration-input"
                        aria-label={t('memberships.customDuration')}
                      />
                      <span className="tier-duration-unit">d</span>
                    </div>
                  )}
                  <NumericInput
                    value={tier.price}
                    onChange={(e) => updateTierPrice(index, parseFloat(e.target.value) || 0)}
                    min="0"
                    step="0.01"
                    className="tier-price-input"
                  />
                  <label className="tier-default-label">
                    <input
                      type="radio"
                      name={defaultTierName}
                      checked={tier.isDefault}
                      onChange={() => setDefaultTier(index)}
                    />
                    {t('memberships.defaultTier')}
                  </label>
                  {isCustom && (
                    <Button
                      type="button"
                      variant="danger"
                      size="sm"
                      onClick={() => removeCustomTier(index)}
                      title={t('common.delete')}
                    >
                      &times;
                    </Button>
                  )}
                </div>
              );
            })}
            <Button type="button" variant="ghost" size="sm" onClick={addCustomTier}>
              + {t('memberships.addCustomDuration')}
            </Button>
          </div>
        </FormField>
      )}
    </>
  );
};

export default PricingTiersField;
